import type * as Types from 'app/types/graphql';

import {
  CampaignGroupWithChildrenSelectFragmentDoc,
  CampaignGroupSelectFragmentDoc,
  CampaignGroupVersionSelectFragmentDoc,
} from '../fragments/campaignGroup.generated';
import {
  CampaignWithProductSelectFragmentDoc,
  CampaignProductSelectFragmentDoc,
} from '../fragments/campaignProduct.generated';
import {
  CampaignSelectFragmentDoc,
  CampaignVersionSelectFragmentDoc,
} from '../fragments/campaign.generated';
import {
  VariantWithTemplateSelectFragmentDoc,
  VariantSelectFragmentDoc,
} from '../fragments/variant.generated';
import { VariantTemplateSelectFragmentDoc } from '../fragments/variantTemplate.generated';
import { VariantPriceSelectFragmentDoc } from '../fragments/variantPrice.generated';
import { VariantProductVariantSelectFragmentDoc } from '../fragments/variantProductVariant.generated';
import { VariantUrlSelectFragmentDoc } from '../fragments/variantUrl.generated';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { appFetcher } from '~/api/utils/gatewayClient';
export type CampaignGroupUpdateMutationVariables = Types.Exact<{
  id: Types.Scalars['ID']['input'];
  input: Types.UpdateCampaignGroupInput;
}>;

export type CampaignGroupUpdateMutationResponse = {
  campaignGroupUpdate?: Types.Maybe<
    Pick<
      Types.CampaignGroup,
      | 'id'
      | 'type'
      | 'title'
      | 'status'
      | 'priority'
      | 'trafficRate'
      | 'targetDevices'
      | 'targetChannels'
      | 'targetCountries'
      | 'targetVisitorTypes'
      | 'conditions'
      | 'createdAt'
      | 'updatedAt'
      | 'startedAt'
    > & {
      campaigns?: Types.Maybe<
        Array<
          Types.Maybe<
            Pick<
              Types.Campaign,
              | 'id'
              | 'name'
              | 'type'
              | 'status'
              | 'priority'
              | 'storeDomain'
              | 'targetDevices'
              | 'targetChannels'
              | 'targetCountries'
              | 'targetVisitorTypes'
              | 'conditions'
              | 'createdAt'
              | 'updatedAt'
              | 'startedAt'
              | 'deletedAt'
              | 'completedAt'
            > & {
              product?: Types.Maybe<
                Pick<
                  Types.CampaignProduct,
                  'id' | 'baseID' | 'title' | 'image' | 'handle' | 'createdAt' | 'updatedAt'
                >
              >;
              variants?: Types.Maybe<
                Array<
                  Types.Maybe<
                    Pick<
                      Types.Variant,
                      | 'id'
                      | 'isOrigin'
                      | 'isWinner'
                      | 'trafficRate'
                      | 'themePageID'
                      | 'templateType'
                      | 'templateSuffix'
                      | 'createdAt'
                      | 'deletedAt'
                      | 'updatedAt'
                      | 'winnerTemplateSuffix'
                    > & {
                      template?: Types.Maybe<
                        Pick<
                          Types.VariantTemplate,
                          | 'createdAt'
                          | 'deletedAt'
                          | 'id'
                          | 'redirectURL'
                          | 'themeID'
                          | 'updatedAt'
                          | 'thumbnailURL'
                          | 'title'
                        >
                      >;
                      price?: Types.Maybe<
                        Pick<
                          Types.VariantPrice,
                          | 'id'
                          | 'baseID'
                          | 'handle'
                          | 'status'
                          | 'title'
                          | 'redirectURL'
                          | 'sourceProductBaseID'
                        >
                      >;
                      productVariants?: Types.Maybe<
                        Array<
                          Types.Maybe<
                            Pick<
                              Types.VariantProductVariant,
                              'id' | 'baseID' | 'name' | 'price' | 'compareAtPrice' | 'variantID'
                            >
                          >
                        >
                      >;
                      url?: Types.Maybe<
                        Pick<
                          Types.VariantUrl,
                          'id' | 'baseID' | 'handle' | 'title' | 'redirectURL' | 'resourceType'
                        >
                      >;
                    }
                  >
                >
              >;
              group?: Types.Maybe<Pick<Types.CampaignGroup, 'id' | 'title' | 'type'>>;
              versions?: Types.Maybe<
                Array<
                  Types.Maybe<
                    Pick<
                      Types.SplitTestingCampaignVersion,
                      'id' | 'version' | 'settings' | 'startedAt' | 'completedAt'
                    >
                  >
                >
              >;
            }
          >
        >
      >;
      versions?: Types.Maybe<
        Array<
          Types.Maybe<
            Pick<
              Types.CampaignGroupVersion,
              'id' | 'version' | 'settings' | 'startedAt' | 'completedAt'
            >
          >
        >
      >;
    }
  >;
};

export const CampaignGroupUpdateDocument = `
    mutation CampaignGroupUpdate($id: ID!, $input: UpdateCampaignGroupInput!) {
  campaignGroupUpdate(id: $id, input: $input) {
    ...CampaignGroupWithChildrenSelect
  }
}
    ${CampaignGroupWithChildrenSelectFragmentDoc}
${CampaignGroupSelectFragmentDoc}
${CampaignGroupVersionSelectFragmentDoc}
${CampaignWithProductSelectFragmentDoc}
${CampaignSelectFragmentDoc}
${CampaignVersionSelectFragmentDoc}
${CampaignProductSelectFragmentDoc}
${VariantWithTemplateSelectFragmentDoc}
${VariantSelectFragmentDoc}
${VariantTemplateSelectFragmentDoc}
${VariantPriceSelectFragmentDoc}
${VariantProductVariantSelectFragmentDoc}
${VariantUrlSelectFragmentDoc}`;

export const useCampaignGroupUpdateMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    CampaignGroupUpdateMutationResponse,
    TError,
    CampaignGroupUpdateMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    CampaignGroupUpdateMutationResponse,
    TError,
    CampaignGroupUpdateMutationVariables,
    TContext
  >(
    ['CampaignGroupUpdate'],
    (variables?: CampaignGroupUpdateMutationVariables) =>
      appFetcher<CampaignGroupUpdateMutationResponse, CampaignGroupUpdateMutationVariables>(
        CampaignGroupUpdateDocument,
        variables,
      )(),
    options,
  );
};

useCampaignGroupUpdateMutation.getKey = () => ['CampaignGroupUpdate'];
